import { extendTheme } from '@chakra-ui/react';

// Lake Lucien Surgery Center — Rebrand (Sora + Public Sans, teal-ink + cyan)
const theme = extendTheme({
  colors: {
    // Primary cyan-blue (design gradient anchors #19C2DA → #2196F3)
    brand: {
      50: '#e7f8fc',
      100: '#c3ecf6',
      200: '#8fdcee',
      300: '#4fc9e2',
      400: '#19C2DA', // gradient start
      500: '#1E9EE8', // primary brand blue
      600: '#1596B8', // accent teal (links, eyebrows)
      700: '#1372B8',
      800: '#0E4257',
      900: '#0E2F3A', // deep teal ink (headings)
    },
    // Star of Life cyan/turquoise (kept for existing references; retuned to design cyan)
    starOfLife: {
      50: '#e6fafd',
      100: '#c0f1f7',
      200: '#8fe6f2',
      300: '#4dd8ea',
      400: '#26cce0',
      500: '#19C2DA', // main cyan
      600: '#14b7d6',
      700: '#12658A',
      800: '#0E4257',
      900: '#0B2734',
    },
    // Standout amber, used only for the price-transparency CTAs. Deliberately
    // outside the cyan-blue palette: the whole point of the estimate link is
    // that it reads as a distinct action instead of blending into the brand.
    accent: {
      50: '#FFF8E8',
      100: '#FFEDC4',
      200: '#FFDD92',
      300: '#FFC94D',
      400: '#FFB020',
      500: '#F59300',
      600: '#C97400',
      700: '#9A5800',
      800: '#6E3F00',
    },
    // Deep ink neutrals from the design (text + muted)
    ink: {
      900: '#0E2F3A',
      800: '#123842',
      700: '#3A5560',
      600: '#4A6570',
      500: '#5C6B76',
      400: '#9AA6AE',
    },
    // Barely-tinted neutral surfaces for alternating sections. Replaces the
    // pale cyan gradients some sections used, which read as brand color
    // bleeding everywhere rather than as a calm surface.
    mist: {
      50: '#F7FAFB',
      100: '#EFF4F6',
      200: '#E3ECEF',
    },
  },
  fonts: {
    heading: "'Sora', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '0.9375rem',  // 15px
    lg: '1.0625rem',  // 17px
    xl: '1.1875rem',  // 19px
    '2xl': '1.375rem', // 22px
    '3xl': '1.75rem',  // 28px
    '4xl': '2.125rem', // 34px
    '5xl': '2.75rem',  // 44px
    '6xl': '3.5rem',   // 56px
    '7xl': '4.25rem',  // 68px (hero)
  },
  breakpoints: {
    sm: '30em',    // 480px
    md: '48em',    // 768px
    lg: '62em',    // 992px
    xl: '80em',    // 1280px
    // Custom: the full text nav needs ~1400px before items start to crowd,
    // so the header swaps to the hamburger below this. Must stay in
    // ascending order — Chakra generates min-width queries from this map.
    nav: '87.5em', // 1400px
    '2xl': '96em', // 1536px
  },
  sizes: {
    container: {
      // Wider page shell so sections stop feeling narrow on large monitors.
      // Chakra's default container.xl is 1280px.
      xl: '1600px',
    },
  },
  radii: {
    xl: '18px',
    '2xl': '24px',
    '3xl': '30px',
    pill: '100px',
  },
  shadows: {
    // Shadows are ink, not brand blue. A blue-tinted glow under every card
    // is what made the whole site look like it was floating in haze; a short
    // contact shadow plus a long soft falloff reads as an object on a surface.
    card: '0 1px 2px rgba(14,47,58,0.05), 0 18px 40px -22px rgba(14,47,58,0.24)',
    cardSoft: '0 1px 2px rgba(14,47,58,0.04), 0 14px 32px -22px rgba(14,47,58,0.18)',
    brand: '0 10px 26px -14px rgba(14,47,58,0.45)',
    float: '0 12px 34px -18px rgba(14,47,58,0.4)',
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  styles: {
    global: {
      body: {
        bg: '#FFFFFF',
        color: 'ink.800',
        fontSize: 'md',
        lineHeight: '1.5',
        letterSpacing: '-0.01em',
      },
      p: {
        lineHeight: '1.62',
      },
      html: {
        scrollBehavior: 'smooth',
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: 'pill',
        fontFamily: "'Public Sans', sans-serif",
      },
      variants: {
        // Primary call to action. The name is kept so every existing usage
        // updates in place, but it is no longer a gradient: a solid deep-ink
        // pill reads as considered and expensive, where a glowing cyan-to-blue
        // gradient is the most recognisable template default there is.
        brandGradient: {
          bg: 'brand.900',
          color: 'white',
          fontWeight: '600',
          boxShadow: 'brand',
          transition: 'transform .35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow .35s, background-color .2s',
          _hover: {
            bg: 'brand.800',
            transform: 'translateY(-2px)',
            boxShadow: '0 16px 34px -16px rgba(14,47,58,0.55)',
            _disabled: { transform: 'none' },
          },
          _active: { transform: 'translateY(0)' },
        },
        // White pill on dark/gradient backgrounds
        brandOnDark: {
          bg: 'white',
          color: 'brand.900',
          fontWeight: '600',
          boxShadow: '0 10px 30px -14px rgba(14,47,58,0.55)',
          transition: 'transform .35s cubic-bezier(0.16, 1, 0.3, 1)',
          _hover: { transform: 'translateY(-2px)' },
        },
      },
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Link: {
      baseStyle: {
        color: 'brand.600',
        _hover: {
          textDecoration: 'none',
          color: 'brand.900',
        },
      },
    },
    Text: {
      baseStyle: {
        lineHeight: '1.62',
        letterSpacing: '-0.01em',
      },
    },
    Heading: {
      baseStyle: {
        fontFamily: "'Sora', sans-serif",
        // 600, not 700 or 800. Sora is a wide geometric face and gets heavy
        // fast; at 600 with a gentler squeeze it reads composed. Pages that
        // still set fontWeight="800" explicitly are being brought down to
        // match.
        fontWeight: '600',
        letterSpacing: '-0.022em',
        lineHeight: '1.1',
        // No global color — headings inherit from their section (dark ink on
        // white sections, white on gradient/dark sections). Setting it here
        // forced dark text onto the hero and CTA gradients.
      },
    },
  },
});

export default theme;
